import { z } from "zod";

import {
  type AssociationRecord,
  AssociationType,
} from "@/shared/domain/association";
import {
  CanonicalDataType,
  MessagePathPartType,
  RepresentationStorageFormat,
} from "@/shared/domain/topics";

const messagePathAttrSchema = z.strictObject({
  type: z.literal(MessagePathPartType.Attr),
  attribute: z.string(),
  dataType: z.enum(CanonicalDataType),
});

const messagePathSliceSchema = z.strictObject({
  type: z.literal(MessagePathPartType.Slice),
  start: z.number(),
  end: z.number().nullable().optional(),
});

const messagePathPartSchema = z.union([
  messagePathAttrSchema,
  messagePathSliceSchema,
]);

const associationSchema: z.ZodType<AssociationRecord> = z.object({
  association_id: z.string(),
  association_type: z.enum([
    AssociationType.Dataset,
    AssociationType.File,
  ] as const),
  association_version: z.number().nullable().optional(),
});

const representationSchema = z.object({
  association: associationSchema,
  id: z.string(),
  format: z.enum(RepresentationStorageFormat),
});

export const topicDataSchema = z.object({
  topic: z.looseObject({
    id: z.string(), // TopicRecord::topic_id
    name: z.string(), // TopicRecord::topic_name
    association: associationSchema, // TopicRecord::association
    startTime: z.string().nullable().optional(), // TopicRecord::start_time
    endTime: z.string().nullable().optional(), // TopicRecord::end_time
    messageCount: z.number().nullable().optional(), // TopicRecord::message_count
  }),
  messagePath: z.object({
    id: z.string(), // MessagePathRecord::topic_message_path_id
    dotPath: z.string(), // MessagePathRecord::source_path, e.g., "transforms.transform.translation.x"
    metadata: z.record(z.string(), z.unknown()), // MessagePathRecord::metadata
    // Ordered list of parts that make up the message path.
    // E.g., for a message path like "transforms.transform.translation.x"
    // this would be:
    // [
    //   { type: "Attr", attribute: "transforms", dataType: <CanonicalDataType.Array> },
    //   { type: "Slice", start: 0, end: 10, step: 1 },
    //   { type: "Attr", attribute: "transform", dataType: <CanonicalDataType.Object> },
    //   { type: "Attr", attribute: "translation", dataType: <CanonicalDataType.Object> },
    //   { type: "Attr", attribute: "x", dataType: <CanonicalDataType.Number> },
    // ]
    parts: z.array(messagePathPartSchema),
  }),
  representation: representationSchema,
});

export interface TopicDataPathBinding {
  fileId: string;
  path: string;
}

export type TopicData = z.infer<typeof topicDataSchema>;
